import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.addEventListener('pointerdown', this.#toggleTootoHoverZoom, { signal });
    this.addEventListener('pointermove', this.#handleTootoHoverZoomMove, { signal });
    this.addEventListener('pointerleave', this.#resetTootoHoverZoom, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    if (this.hasAttribute('data-tooto-disable-variant-media-switching')) return;

    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Toggles the desktop inline zoom on image click.
   * @param {PointerEvent} event - The pointer event.
   */
  #toggleTootoHoverZoom = (event) => {
    if (!window.matchMedia('(min-width: 750px)').matches) return;

    const mediaContainer = this.#getTootoHoverZoomContainer(event);
    if (!mediaContainer) return;

    event.preventDefault();

    const isZooming = mediaContainer.classList.contains('tooto-product-media-hover-zooming');
    this.#resetTootoHoverZoom();

    if (isZooming) return;

    mediaContainer.classList.add('tooto-product-media-hover-zooming');
    this.#setTootoHoverZoomOrigin(mediaContainer, event);
  };

  /**
   * Moves the desktop inline zoom focal point with the pointer.
   * @param {PointerEvent} event - The pointer event.
   */
  #handleTootoHoverZoomMove = (event) => {
    if (!window.matchMedia('(min-width: 750px)').matches) return;

    const mediaContainer = this.#getTootoHoverZoomContainer(event);
    if (!mediaContainer || !mediaContainer.classList.contains('tooto-product-media-hover-zooming')) return;

    this.#setTootoHoverZoomOrigin(mediaContainer, event);
  };

  /**
   * @param {PointerEvent} event - The pointer event.
   * @returns {HTMLElement | null}
   */
  #getTootoHoverZoomContainer(event) {
    const target = event.target;
    if (!(target instanceof Element)) return null;

    const mediaContainer = target.closest('.product-media-container--image');
    if (!(mediaContainer instanceof HTMLElement) || mediaContainer.closest('.dialog-zoomed-gallery')) {
      return null;
    }

    const image = mediaContainer.querySelector('.product-media__image');
    if (!(image instanceof HTMLElement)) return null;

    return mediaContainer;
  }

  /**
   * @param {HTMLElement} mediaContainer - The media container to update.
   * @param {PointerEvent} event - The pointer event.
   */
  #setTootoHoverZoomOrigin(mediaContainer, event) {
    const rect = mediaContainer.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    mediaContainer.style.setProperty('--tooto-product-media-hover-x', `${Math.max(0, Math.min(100, x))}%`);
    mediaContainer.style.setProperty('--tooto-product-media-hover-y', `${Math.max(0, Math.min(100, y))}%`);
  }

  #resetTootoHoverZoom = () => {
    this.querySelectorAll('.tooto-product-media-hover-zooming').forEach((mediaContainer) => {
      if (!(mediaContainer instanceof HTMLElement)) return;

      mediaContainer.classList.remove('tooto-product-media-hover-zooming');
      mediaContainer.style.removeProperty('--tooto-product-media-hover-x');
      mediaContainer.style.removeProperty('--tooto-product-media-hover-y');
    });
  };

  /**
   * Selects the mobile static media item without scrolling the slideshow.
   * @param {number|string} index - The index of the media to show.
   * @param {PointerEvent} event - The pointer event.
   */
  selectTootoMobileMedia(index, event) {
    event?.preventDefault();

    const selectedIndex = Number(index);
    if (Number.isNaN(selectedIndex)) return;

    const selectedMedia = this.querySelector(`[data-tooto-product-media-main="${selectedIndex}"]`);

    selectedMedia?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start',
    });

    this.#setTootoMobileMedia(selectedIndex);
  }

  /**
   * Syncs thumbnails to the mobile media track's visible slide.
   * @param {Event} event - The scroll event from the mobile media track.
   */
  syncTootoMobileMedia(event) {
    const track = event.currentTarget;
    if (!(track instanceof HTMLElement)) return;

    const mediaItems = [...track.querySelectorAll('[data-tooto-product-media-main]')];
    if (!mediaItems.length) return;

    const trackRect = track.getBoundingClientRect();
    const trackCenter = trackRect.left + trackRect.width / 2;
    let selectedIndex = 0;
    let shortestDistance = Infinity;

    for (const media of mediaItems) {
      const mediaRect = media.getBoundingClientRect();
      const mediaCenter = mediaRect.left + mediaRect.width / 2;
      const distance = Math.abs(mediaCenter - trackCenter);

      if (distance < shortestDistance) {
        const index = Number(media.getAttribute('data-tooto-product-media-main'));
        if (Number.isNaN(index)) continue;

        selectedIndex = index;
        shortestDistance = distance;
      }
    }

    this.#setTootoMobileMedia(selectedIndex);
  }

  /**
   * Updates the selected mobile media state.
   * @param {number} selectedIndex - The selected media index.
   */
  #setTootoMobileMedia(selectedIndex) {
    const selectedIndexValue = String(selectedIndex);
    const previousSelectedThumbnail = this.querySelector('[data-tooto-product-media-thumb][aria-selected="true"]');
    const previousSelectedIndex = previousSelectedThumbnail?.getAttribute('data-tooto-product-media-thumb');

    if (previousSelectedIndex === selectedIndexValue) return;

    this.querySelectorAll('[data-tooto-product-media-main]').forEach((media) => {
      const isSelected = media.getAttribute('data-tooto-product-media-main') === selectedIndexValue;
      media.setAttribute('aria-hidden', String(!isSelected));
    });

    this.querySelectorAll('[data-tooto-product-media-thumb]').forEach((thumbnail) => {
      const isSelected = thumbnail.getAttribute('data-tooto-product-media-thumb') === selectedIndexValue;
      thumbnail.setAttribute('aria-selected', String(isSelected));

      if (isSelected) {
        thumbnail.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    });
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
